/**
 * Static route-to-telemetry-persona mapping.
 *
 * The TelemetryProvider reads this config to determine how each page
 * should be tracked (identity, events, etc.) without any per-page hooks.
 *
 * Personas:
 * - "unauthenticated": No identity query, fires "Page Loaded" from router events
 * - "standalone": Fetches identity, fires "Page Loaded" from router events
 * - "embedded": Fetches identity, fires "Embedded View Loaded" once on mount
 * - "excluded": No tracking at all
 */

export type TelemetryPersona =
  | "unauthenticated"
  | "standalone"
  | "embedded"
  | "excluded";

export type RouteConfigEntry = {
  persona: TelemetryPersona;
  /** Human-readable view name sent to Mixpanel. Required for embedded persona. */
  viewName?: string;
};

const ROUTE_CONFIG: Record<string, RouteConfigEntry> = {
  // Unauthenticated
  "/login": { persona: "unauthenticated" },
  "/login/forget": { persona: "unauthenticated" },
  "/login/redirect": { persona: "unauthenticated" },
  "/signup": { persona: "unauthenticated" },
  "/first": { persona: "unauthenticated" },

  // Standalone authenticated
  "/": { persona: "standalone" },

  // Embedded authenticated
  "/billing": { persona: "embedded", viewName: "Billing" },
  "/upgrade": { persona: "embedded", viewName: "Upgrade" },
  "/team/waitlist": { persona: "embedded", viewName: "Waitlist" },
  "/team/unscheduled-work": {
    persona: "embedded",
    viewName: "Unscheduled Work",
  },
  "/events": {
    persona: "embedded",
    viewName: "Events",
  },
  "/calendar": {
    persona: "embedded",
    viewName: "Event Calendar",
  },
  "/dispatch": { persona: "embedded", viewName: "Dispatch Board" },
  "/partials/header": { persona: "embedded", viewName: "Header" },
  "/partials/drawer": { persona: "embedded", viewName: "Estimate Drawer" },
  "/partials/modal": {
    persona: "embedded",
    viewName: "Visit Selection Modal",
  },
  "/partials/waitlist-drawer": {
    persona: "embedded",
    viewName: "Waitlist Drawer",
  },

  // Excluded from tracking
  "/finances/estimates": { persona: "excluded" },
  "/robots.txt": { persona: "excluded" },
  "/sitemap.xml": { persona: "excluded" },
  "/test-sentry": { persona: "excluded" },
  "/version": { persona: "excluded" },
};

/**
 * Look up the telemetry config for a given pathname.
 * Falls back to "standalone" for unknown routes so new pages are
 * tracked by default without explicit registration.
 */
export function getRouteConfig(pathname: string): RouteConfigEntry {
  return ROUTE_CONFIG[pathname] ?? { persona: "standalone" };
}

/** Whether this persona requires fetching identity (user/company) data. */
export function requiresIdentity(persona: TelemetryPersona): boolean {
  return persona === "standalone" || persona === "embedded";
}
